import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { unwrapResult } from '@reduxjs/toolkit';
import WalletIcon from '@mui/icons-material/Wallet';
import AtmIcon from '@mui/icons-material/LocalAtm';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { VerifyMemberPaypalIdentityInput } from '@optimus/models';
import Decimal from 'decimal.js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  selectPayPalAccount,
  selectGetCompanyPartnerRelationsState,
  selectGetCompanyRewardsWalletState,
  selectGetCompanyPayoutsWalletState,
  selectCompanyRewardsWallet,
  selectCompanyPayoutsWallet,
  selectBankAccount,
  selectPayPalSetAsDefault,
} from '../../../store/slices/dashboardSlice';
import {
  activatePaymentMethod,
  CompanyWalletTransactions,
  getCompanyPartnerRelations,
  getCompanyPayoutsWallet,
  getCompanyRewardsWallet,
  getPaymentMethods,
  getRecentRebatePartners,
  verifyAndUpdatePaypalEmail,
} from '../../../store/thunks/dashboardThunk';
import { OuterContainer } from '../DashboardOuterContainerPagesLayout';
import { WalletSmallInfoCard } from '../../../common/WalletSmallInfoCard';
import { WalletRewardsBalanceCard } from '../../../common/WalletRewardsBalanceCard';
import { WalletListInfoCardSkeleton } from '../../../common/WalletListInfoCardsSkeleton';
import { WalletSmallInfoCardSkeleton } from '../../../common/WalletSmallInfoCardSkeleton';
import { WalletRewardsBalanceCardSkeleton } from '../../../common/WalletRewardsBalanceCardSkeleton';
import { selectGetOnboardingFormStatus, selectMemberInformation, selectMemberSession } from '../../../store/slices/onboardingSlice';
import { TestIds } from '../../../assets/testIds';
import { PayoutSettings } from './PayoutSettings';
import { PARTNERS_ROUTE } from '../../../routes/namedRoutes';
import { WalletListType, PaymentDetailStatusType } from '../../../common/enums';
import { TransactionsList } from '../../../common/TransactionsList';
import { PartnersList } from '../../../common/PartnersList';
import { useSolutionOrg } from '../../../config/useSolutionOrg';
import { getOnboardingForm } from '../../../store/thunks/onboardingThunk';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { ManagePayoutSettingsDialog } from './ManagePayoutSettingsDialog';
import { PayPalAccount } from '../../../common/models/PaymentMethod';
import { Logger } from '../../../config/logging/logger';
import { useTrackedChecklistUpdate } from '../Home/useTrackedChecklistUpdate';
import { useServiceNationMember } from './useServiceNationMember';
import { HistoricalRebateData } from './HistoricalRebateData';
import { AccessResource, useHasAccess } from '../../../common/hooks/useHasAccess';

export function Wallet() {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { company } = useAppSelector(selectMemberInformation);
  const payPalAccount = useAppSelector(selectPayPalAccount);
  const bankAccount = useAppSelector(selectBankAccount);
  const getCompanyRewardsState = useAppSelector(selectGetCompanyRewardsWalletState);
  const getCompanyPayoutsState = useAppSelector(selectGetCompanyPayoutsWalletState);
  const companyRewardsWallet = useAppSelector(selectCompanyRewardsWallet);
  const companyPayoutsWallet = useAppSelector(selectCompanyPayoutsWallet);
  const getCompanyPartnerRelationsState = useAppSelector(selectGetCompanyPartnerRelationsState);
  const [payoutSettingsLoading, setPayoutSettingsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const getOnboardingFormStatus = useAppSelector(selectGetOnboardingFormStatus);
  const [modalType, setModalType] = useState<WalletListType | null>(null);
  const [loading, setLoading] = useState(false);
  const { envSolutionOrgFormId } = useSolutionOrg();
  const { trackRequest, trackPayoutSetting } = useCustomEventTracking();
  const paypalAuthCode = searchParams.get('code');
  const { memberId } = useAppSelector(selectMemberSession);
  const payPalSetAsDefault = useAppSelector(selectPayPalSetAsDefault);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const logger = useMemo(() => Logger.create('Wallet'), []);
  const hasHistoricalRebateAccess = useHasAccess(AccessResource.HistoricalRebate);
  const canEditPayoutMethods = useHasAccess(AccessResource.ManagePayoutMethods);

  // Updating checklist step2 as completed
  useTrackedChecklistUpdate(t('checklist.steps.step2.key'));

  useEffect(() => {
    setErrorMessage(`${t('error.generalMessage')}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (company?.id) {
      dispatch(getCompanyPartnerRelations(company.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  useEffect(() => {
    if (company?.id) {
      setLoading(true);
      dispatch(getCompanyRewardsWallet(company.id))
        .catch((error) => logger.error(Logger.format('Failed to fetch company wallet:', error)));
    }
  }, [company?.id, dispatch, logger]);

  useEffect(() => {
    if (company?.id) {
      dispatch(getCompanyPayoutsWallet(company.id))
        .catch((error) => logger.error(Logger.format('Failed to fetch company wallet:', error)));
    }
  }, [company?.id, dispatch, logger]);

  useEffect(() => {
    dispatch(getOnboardingForm({
      formId: (envSolutionOrgFormId as string) || '',
      trackRequest,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isLoading =
      (getCompanyRewardsState === 'pending' && getCompanyPayoutsState === 'pending') ||
      getCompanyPartnerRelationsState === 'pending' ||
      getOnboardingFormStatus === 'pending';

    setLoading(isLoading);
  }, [
    getCompanyRewardsState,
    getCompanyPayoutsState,
    getCompanyPartnerRelationsState,
    getOnboardingFormStatus,
  ]);

  // remove query params added by paypal from return url
  const removePaypalQueryParams = () => {
    const authCode = searchParams.get('code');
    if (authCode) {
      searchParams.delete('code');
      setSearchParams(searchParams);
    }
    const scope = searchParams.get('scope');
    if (scope) {
      searchParams.delete('scope');
      setSearchParams(searchParams);
    }
  };

  useEffect(() => {
    if (paypalAuthCode) {
      const request: VerifyMemberPaypalIdentityInput = {
        authToken: paypalAuthCode,
      };
      dispatch(verifyAndUpdatePaypalEmail({
        memberId,
        data: request,
      }))
        .then(unwrapResult)
        .then((res: PayPalAccount) => {
          removePaypalQueryParams();
          trackPayoutSetting('Complete', 'ConnectWithPayPal');
          if (payPalSetAsDefault) {
            dispatch(activatePaymentMethod({ paymentMethodId: res.id }))
              .then(() => {
                trackPayoutSetting('Click', 'PayPalDefault');
                dispatch(getPaymentMethods());
              })
              .catch((error: unknown) => logger.error(Logger.format(error)));
          }
          setOpenErrorSnackbar(false);
        })
        /* eslint-disable @typescript-eslint/no-explicit-any */
        .catch((error: any) => {
          setErrorMessage(error.errorMessage || t('error.generalMessage'));
          removePaypalQueryParams();
          setOpenErrorSnackbar(true);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paypalAuthCode]);

  const companyTransactions = useMemo(
    () => [
      ...(companyRewardsWallet?.transactions ?? []),
      ...(companyPayoutsWallet?.transactions ?? []),
    ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [companyRewardsWallet?.transactions, companyPayoutsWallet?.transactions],
  );
  const hasTransactions = companyTransactions.length > 0;
  const userPayoutEnrolled = (payPalAccount?.status === 'verified' || bankAccount?.status === 'verified');
  const companyBalance = companyRewardsWallet?.balance ? new Decimal(companyRewardsWallet.balance).toFixed(2) : '0.00';
  const {
    isServiceNationPaidWithAccess,
  } = useServiceNationMember();

  useEffect(() => {
    if (memberId && isServiceNationPaidWithAccess) {
      dispatch(getRecentRebatePartners({ memberId }))
        .unwrap()
        .catch((error) => logger.error(Logger.format('Failed to fetch recent rebate partners', error)));
    }
  }, [memberId, isServiceNationPaidWithAccess, dispatch, logger]);

  const showWalletRewardsBalanceCard = useMemo(() => {
    const noPayoutSetup = !payoutSettingsLoading && !userPayoutEnrolled;
    const hasBalance = companyBalance !== '0.00';
    return (noPayoutSetup && hasTransactions && hasBalance) || !hasTransactions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payoutSettingsLoading,
    userPayoutEnrolled,
    hasTransactions,
    companyBalance,
    payPalAccount,
  ]);

  const openModal = (
    type: WalletListType.PAYOUT | WalletListType.TRANSACTIONS | WalletListType.PARTNERSRELATION,
  ) => setModalType(type);
  const closeModal = () => setModalType(null);
  const clickRedirectPartners = () => {
    navigate(PARTNERS_ROUTE);
  };

  const getMostRecentPayout = (data: CompanyWalletTransactions[]) => {
    const mostRecentPayout = data
      .filter(
        (item) => item.type === 'payout' && item.status === PaymentDetailStatusType.PAYMENT_SUCCEEDED,
      )[0];

    if (mostRecentPayout) {
      return (mostRecentPayout.amount ? new Decimal(mostRecentPayout.amount).toFixed(2) : '0.00');
    }
    return undefined;
  };

  const getMostRecentDateByType = (data: CompanyWalletTransactions[], type: string) => {
    const mostRecentItem = data
      .filter((item) => item.type === type)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    if (mostRecentItem) {
      const formattedDate = moment(mostRecentItem.createdAt).format('MMMM D, YYYY');
      return formattedDate;
    }
    return undefined;
  };

  const handleSnackbarClose = () => {
    setOpenErrorSnackbar(false);
  };

  return (
    <OuterContainer
      sx={{
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        padding: theme.spacing(0),
        paddingTop: theme.spacing(4),
      }}
    >
      <>
        <Snackbar
          open={openErrorSnackbar}
          autoHideDuration={2000}
          onClose={handleSnackbarClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {errorMessage}
          </Alert>
        </Snackbar>
        <Box
          sx={{
            width: '100%',
            marginBottom: theme.spacing(5),
          }}
        >
          <Typography variant="h12">
            {t('wallet.wallet')}
          </Typography>
        </Box>
        <Grid
          container
          sx={{
            width: '100%',
            flexWrap: 'nowrap',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* First Column */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              paddingRight: { xs: 0, md: theme.spacing(2.5) },
              paddingBottom: { xs: theme.spacing(2.5), md: 0 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                gap: theme.spacing(2.5),
              }}
            >
              {/* For SN native members, show Last Payout full width */}
              {isServiceNationPaidWithAccess && (
                <Box sx={{ width: '100%' }}>
                  {loading && <WalletSmallInfoCardSkeleton />}
                  {!loading && getCompanyPayoutsState === 'pending' && (
                    <WalletSmallInfoCardSkeleton />
                  )}
                  {!loading && getCompanyPayoutsState !== 'pending' && (
                    <WalletSmallInfoCard
                      smallCardId={TestIds.walletPage.smallCardPayout}
                      smallCardTitle={t('wallet.lastPayout')}
                      smallCardContent={`$${getMostRecentPayout(companyTransactions) || '0.00'}`}
                      smallCardSubtitle={getMostRecentDateByType(companyTransactions, 'payout') || t('wallet.rewardsPayout')}
                      SmallCardIcon={AtmIcon}
                      optionalStyles={{ maxWidth: 'none', width: '100%' }}
                    />
                  )}
                </Box>
              )}
              {!isServiceNationPaidWithAccess && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    gap: theme.spacing(2.5),
                  }}
                >
                  {loading ?
                    <>
                      <WalletSmallInfoCardSkeleton />
                      <WalletSmallInfoCardSkeleton />
                    </> :
                    <>
                      {getCompanyRewardsState === 'pending' ? (
                        <WalletSmallInfoCardSkeleton />
                      ) : (
                        <WalletSmallInfoCard
                          smallCardId={TestIds.walletPage.smallCardRewards}
                          smallCardTitle={t('wallet.rewardsEarned')}
                          smallCardContent={`$${companyRewardsWallet?.totalRewards ? new Decimal(companyRewardsWallet?.totalRewards).toFixed(2) : '0.00'}`}
                          smallCardSubtitle={t('wallet.lifeTimeEarnings')}
                          SmallCardIcon={WalletIcon}
                        />
                      )}
                      {getCompanyPayoutsState === 'pending' ? (
                        <WalletSmallInfoCardSkeleton />
                      ) : (
                        <WalletSmallInfoCard
                          smallCardId={TestIds.walletPage.smallCardPayout}
                          smallCardTitle={t('wallet.lastPayout')}
                          smallCardContent={`$${getMostRecentPayout(companyTransactions) || '0.00'}`}
                          smallCardSubtitle={getMostRecentDateByType(companyTransactions, 'payout') || t('wallet.rewardsPayout')}
                          SmallCardIcon={AtmIcon}
                        />
                      )}
                    </>
                  }
                </Box>
              )}
              {loading ?
                <>
                  {<WalletRewardsBalanceCardSkeleton />}
                  {<WalletListInfoCardSkeleton />}
                </> :
                <>
                  {showWalletRewardsBalanceCard &&
                    <WalletRewardsBalanceCard
                      hasRewardsBalance={hasTransactions}
                      rewardsBalanceValue={companyBalance}
                      rewardsBalanceLastUpdate={getMostRecentDateByType(companyTransactions, 'reward') || t('wallet.lifeTimeEarnings')}
                      setModalType={setModalType}
                      // SN-190: sub-accounts granted read-only Wallet
                      // access can't edit payout methods
                      canEditPayoutMethod={canEditPayoutMethods}
                    />}
                  {hasTransactions && <TransactionsList
                    transactionsListId={TestIds.walletPage.transactionList}
                    maxTransactionsShow={3}
                    onBtnClick={() => openModal(WalletListType.TRANSACTIONS)}
                  />}
                  {/*
                    SN-190: gated by SN's historic_rebate_access grant rather
                    than isServiceNationPaidWithAccess - intentionally no
                    longer restricted to paid SN tiers, since SN itself
                    decides who gets this grant.
                  */}
                  {hasHistoricalRebateAccess && (
                    <Box sx={{ width: '100%' }}>
                      <HistoricalRebateData />
                    </Box>
                  )}
                </>
              }
            </Box>
          </Grid>

          {/* Second Column */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                gap: theme.spacing(2.5),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '100%',
                  gap: theme.spacing(2.5),
                }}
              >
                {loading ?
                  <>
                    <WalletSmallInfoCardSkeleton />
                  </> :
                  <>
                    <PayoutSettings
                      onLoadingChange={
                        (componentLoading) => setPayoutSettingsLoading(componentLoading)
                      }
                      // All Edge users or SN master accounts can edit payout methods
                      canEditPayoutMethod={canEditPayoutMethods}
                    />
                  </>
                }
              </Box>
              {loading ?
                <>
                  {<WalletListInfoCardSkeleton />}
                </> :
                <>
                  <PartnersList
                    partnersListId={TestIds.walletPage.partnerList}
                    onBtnClick={() => clickRedirectPartners()}
                    isServiceNationPaidWithAccess={isServiceNationPaidWithAccess}
                  />
                </>
              }
            </Box>
          </Grid>

          {/* Transaction Modal */}
          <Dialog
            open={modalType === WalletListType.TRANSACTIONS}
            onClose={closeModal}
            maxWidth="sm"
            fullWidth
            sx={{
              '& .MuiPaper-root': {
                borderRadius: theme.spacing(2.5),
              },
            }}
          >
            <DialogTitle
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <IconButton
                aria-label="close"
                onClick={closeModal}
                sx={{
                  position: 'absolute',
                  right: theme.spacing(2),
                  top: theme.spacing(2),
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <TransactionsList
                transactionsListId={TestIds.walletPage.transactionList}
                optionalStyles={{ boxShadow: 'none', alignItems: 'center' }}
              />
            </DialogContent>
          </Dialog>

          {/* Payout Modal */}
          {canEditPayoutMethods &&
            <ManagePayoutSettingsDialog
              open={modalType === WalletListType.PAYOUT}
              setOpenDialog={closeModal}
            />}
        </Grid>
      </>
    </OuterContainer>
  );
}
